import { format } from "date-fns";
import { Check, Sparkles } from "lucide-react";

export default function BookSummary({
    cabin, range, nights, guests, lodgingTotal, rateBreakdown,
    experiences, selectedExperiences, discountAmount, tierName, discountPct, finalTotal,
    paymentMethods, selectedMethod, setSelectedMethod, methodLabel,
    onSubmit, submitting, rangeHasBookedDate, formatMoney, t,
}) {
    return (
        <aside className="lg:col-span-4 lg:col-start-9">
            <div className="border border-espresso/15 bg-bone2/40 p-8 lg:sticky lg:top-32" data-testid="booking-summary">
                <div className="eyebrow mb-4">{t("book.summary")}</div>
                <h3 className="font-serif text-2xl mb-2">{cabin?.name || t("book.selectCabin")}</h3>
                <p className="text-sm text-muted2 mb-6">{cabin?.tagline}</p>
                <div className="hairline mb-6" />
                <ul className="text-sm space-y-3 mb-6">
                    <li className="flex justify-between"><span className="text-muted2">{t("book.checkin")}</span><span>{range.from ? format(range.from, "MMM d, yyyy") : "—"}</span></li>
                    <li className="flex justify-between"><span className="text-muted2">{t("book.checkout")}</span><span>{range.to ? format(range.to, "MMM d, yyyy") : "—"}</span></li>
                    <li className="flex justify-between"><span className="text-muted2">{nights === 1 ? t("common.night") : t("common.nights")}</span><span>{nights || "—"}</span></li>
                    <li className="flex justify-between"><span className="text-muted2">{t("book.guests")}</span><span>{guests}</span></li>
                </ul>
                <div className="hairline mb-4" />
                <ul className="text-sm space-y-2 mb-4">
                    <li className="flex justify-between"><span className="text-muted2">{t("book.lodging")} ({nights} {nights === 1 ? "nt" : "nts"})</span><span>{formatMoney(lodgingTotal)}</span></li>
                    {rateBreakdown && rateBreakdown.nights?.some((n) => n.season_multiplier !== 1 || n.weekend_premium_applied) && (
                        <li className="pt-2" data-testid="rate-breakdown">
                            <div className="eyebrow text-espresso/70 mb-2">{t("book.rateBreakdown")}</div>
                            <ul className="space-y-1 text-xs text-muted2 max-h-40 overflow-auto pr-1">
                                {rateBreakdown.nights.map((n) => (
                                    <li key={n.date} className="flex justify-between gap-2">
                                        <span>
                                            {n.weekday} · {n.date.slice(5)}
                                            {n.season_multiplier !== 1 && <span className="ml-1 text-terracotta">· {t("book.seasonRate")}</span>}
                                            {n.weekend_premium_applied && <span className="ml-1 text-forest">· {t("book.weekendPremium")}</span>}
                                        </span>
                                        <span className="text-ink">{formatMoney(n.price)}</span>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                    {Object.entries(selectedExperiences).map(([id, qty]) => {
                        const e = experiences.find((x) => x.id === id);
                        if (!e) return null;
                        return (<li key={id} className="flex justify-between text-xs"><span className="text-muted2">{e.name} × {qty}</span><span>{formatMoney(e.price * qty)}</span></li>);
                    })}
                    {discountAmount > 0 && (
                        <li className="flex justify-between text-terracotta" data-testid="loyalty-discount-line">
                            <span className="inline-flex items-center gap-1.5"><Sparkles size={12} /> {t("loyaltyDiscount.line", tierName, discountPct)}</span>
                            <span>-{formatMoney(discountAmount)}</span>
                        </li>
                    )}
                </ul>
                <div className="hairline mb-6" />
                <div className="flex justify-between items-baseline mb-6">
                    <span className="eyebrow">{t("book.estimatedTotal")}</span>
                    <span className="font-serif text-3xl" data-testid="booking-total">{formatMoney(finalTotal)}</span>
                </div>

                {/* Payment method */}
                <div className="mb-6" data-testid="payment-methods">
                    <div className="eyebrow mb-3">{t("book.step5")}</div>
                    <div className="space-y-2">
                        {paymentMethods.map((m) => (
                            <button
                                key={m.key}
                                type="button"
                                onClick={() => setSelectedMethod(m.key)}
                                data-testid={`pay-method-${m.key}`}
                                className={`w-full text-left border px-4 py-3 text-sm transition-all ${selectedMethod === m.key ? "border-forest bg-forest/5" : "border-espresso/15 hover:border-espresso/40"}`}
                            >
                                <span className="inline-flex items-center gap-2">
                                    {selectedMethod === m.key && <Check size={14} className="text-forest" />}
                                    {methodLabel(m.key)}
                                </span>
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setSelectedMethod("reserve")}
                            data-testid="pay-method-reserve"
                            className={`w-full text-left border px-4 py-3 transition-all ${selectedMethod === "reserve" ? "border-forest bg-forest/5" : "border-espresso/15 hover:border-espresso/40"}`}
                        >
                            <span className="inline-flex items-center gap-2 text-sm">
                                {selectedMethod === "reserve" && <Check size={14} className="text-forest" />}
                                {t("book.reserveLater")}
                            </span>
                            <span className="block text-xs text-muted2 mt-1">{t("book.reserveLaterNote")}</span>
                        </button>
                    </div>
                </div>

                <button onClick={onSubmit} disabled={submitting || rangeHasBookedDate} data-testid="book-submit" className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50">
                    {submitting ? t("book.sending")
                        : selectedMethod === "reserve" ? t("book.request")
                        : t("book.payNow", formatMoney(finalTotal))}
                </button>
                {selectedMethod === "reserve" && (
                    <p className="mt-4 text-xs text-muted2 leading-relaxed">{t("book.noPaymentNote")}</p>
                )}
            </div>
        </aside>
    );
}
